import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { useServerStore } from '@/shell/stores/serverStore'
import TelegramMessageList from '@os/messenger/web/components/telegram/TelegramMessageList'
import TelegramMessageInput, { type TelegramMessageInputHandle } from '@os/messenger/web/components/telegram/TelegramMessageInput'
import ChannelDropZone from './ChannelDropZone'
import type { Message } from '../../types'

/**
 * Text-channel chat surface. Re-uses the polymorphic Telegram* message
 * components so text channels get the same feature set as DMs (sticker
 * picker, GIFs, voice/video messages, sticky-date pill, threads pill,
 * read-receipt overlay). Drag-and-drop upload is preserved via the
 * existing ChannelDropZone wrapper.
 *
 * The earlier `chat/MessageList` + `chat/MessageInput` pair stays in
 * the tree as legacy code paths, but no view mounts them anymore.
 */
export default function ChannelChatView() {
  const { channelId } = useParams<{ channelId?: string }>()
  const currentUser = useAuthStore((s) => s.user)
  const servers = useServerStore((s) => s.servers)
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null)
  // OSPROD-75: hand drag&drop files into the composer's preview+caption
  // modal so DnD shares the paperclip path's UX (thumbnail, caption,
  // Send/Cancel) instead of uploading silently behind the scenes.
  const inputRef = useRef<TelegramMessageInputHandle>(null)

  // Resolve the channel record so we know whether the user can write
  // (admin-only-write channels lock the composer for non-admins). Same
  // server-tree walk pattern as VoiceChannelSidePanel.
  const channelInfo = (() => {
    if (!channelId) return null
    for (const s of servers) {
      const direct = s.channels?.find((c) => c.id === channelId)
      if (direct) return { channel: direct, server: s }
      for (const cat of s.categories || []) {
        const inCat = cat.channels?.find((c) => c.id === channelId)
        if (inCat) return { channel: inCat, server: s }
      }
    }
    return null
  })()
  const isAdminOnly = !!channelInfo?.channel.adminOnlyWrite
  const isOwnerOrAdmin = !!currentUser?.isAdmin || channelInfo?.server.ownerId === currentUser?.id
  const canWrite = !isAdminOnly || isOwnerOrAdmin

  if (!channelId) {
    return (
      <div className="flex-1 flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">No channel selected</div>
      </div>
    )
  }

  return (
    <ChannelDropZone onFilesDropped={(files) => inputRef.current?.openWithFiles(files)}>
      <div className="flex-1 flex flex-col min-h-0 bg-background">
        <TelegramMessageList
          target={{ kind: 'channel', id: channelId, canWrite, channelType: 'TEXT' }}
          onReply={(m) => setReplyToMessage(m)}
        />
        <TelegramMessageInput
          ref={inputRef}
          target={{ kind: 'channel', id: channelId, canWrite, channelType: 'TEXT' }}
          replyToId={replyToMessage?.id || null}
          replyToMessage={replyToMessage || undefined}
          onCancelReply={() => setReplyToMessage(null)}
        />
      </div>
    </ChannelDropZone>
  )
}
